import React, { useState, useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { fetchSegmentClassifications } from "../../redux/actions/SegmentClassificationActions";
import { displayDateFormart, isValidArr } from "../../utility/utils";
import EnhancedTable from "../table/Table";
import PageTitle from "../PageTitle";
import PageSearch from "../PageSearch";
import DialogBox from "../../components/dialog-box/DialogBox";
import SegmentClassificationForm from "./SegmentClassificationForm";
import Loader from "../../components/loader/Loader";
import { SegmentModel } from "../../models/SegmentClassificationModel";

export type Mode = "add" | "edit";

type iData = {
  id: number;
  name: string;
  description: string;
  createdDate: string;
  // status: string;
};

const SegmentClassificationPage = () => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("add");
  const [editData, setEditData] = useState<SegmentModel>();
  const [rootData, setRootData] = useState<iData[]>([]);
  const [filteredData, setFilteredData] = useState<iData[]>([]);

  const allData = useAppSelector(
    (state) => state.segmentClassification.all_SegmentClassifications
  ) as SegmentModel[];

  const isSegmentClassificationsFetching = useAppSelector(
    (state) => state.segmentClassification.isSegmentClassificationsFetching
  );

  const columns = useMemo(() => {
    return [
      {
        id: "name",
        numeric: false,
        disablePadding: false,
        label: "Name",
      },
      {
        id: "description",
        numeric: false,
        disablePadding: false,
        label: "Description",
      },
      {
        id: "createdDate",
        numeric: false,
        disablePadding: false,
        label: "Created Date",
        width: 150,
      },
      // {
      //   id: "status",
      //   numeric: false,
      //   disablePadding: false,
      //   label: "Status",
      // },
    ];
  }, []);

  //To fetch all segment classifications
  useEffect(() => {
    dispatch(fetchSegmentClassifications());
  }, [dispatch]);

  useEffect(() => {
    setRootData(() =>
      allData.map((rec) => ({
        id: rec.segmentclassid,
        name: rec.name,
        description: rec.description,
        createdDate: displayDateFormart(rec.createdDate),
        // status: rec.isActive ? "Active" : "Inactive",
      }))
    );
  }, [allData]);

  // To set filtered data on change root data
  useEffect(() => {
    setFilteredData(rootData);
  }, [rootData]);

  const handleEdit = (id: string | number) => {
    const editRec = allData.find((rec) => rec.segmentclassid === id);
    setMode("edit");
    setEditData(editRec);
    setIsModalOpen(true);
  };

  const createHandler = () => {
    setMode("add");
    setEditData(undefined);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const searchHandler = (searchVal: string): void => {
    setFilteredData(() =>
      searchVal
        ? rootData.filter((info) =>
            Object.values(info).join("").toLowerCase().includes(searchVal)
          )
        : rootData
    );
  };

  return (
    <>
      {!isValidArr(allData) && isSegmentClassificationsFetching ? (
        <Loader />
      ) : null}
      <PageTitle
        title="Segment Classifications"
        btnText="Add Segment Classification"
        onClick={createHandler}
      />
      <PageSearch
        placeholder="Search Segment Classifications"
        searchHandler={searchHandler}
      />
      <EnhancedTable
        columns={columns}
        data={filteredData}
        handleEdit={handleEdit}
      />
      {isModalOpen && (
        <DialogBox
          isModalOpen={isModalOpen}
          modalTitle={
            mode === "add"
              ? "Create Segment Classification"
              : "Edit Segment Classification"
          }
          handleModalClose={handleModalClose}
        >
          <SegmentClassificationForm
            mode={mode}
            data={editData}
            handleModalClose={handleModalClose}
          />
        </DialogBox>
      )}
    </>
  );
};

export default SegmentClassificationPage;
